import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  OnInit,
  viewChild,
  input,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { MatTableDataSource } from '@angular/material/table';
import { TranslatePipe } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material/material.module';
import { CollectionUsersResponse } from 'src/app/models/services/graph/graph-service-response.model';
import { UserGraph } from 'src/app/models/user-graph.model';
import { UserInfo } from 'src/app/models/user-info.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { ToolBoxService } from 'src/app/services/toolbox/toolbox.service';

@Component({
  selector: 'app-users-table',
  imports: [CommonModule, MaterialModule, TranslatePipe],
  templateUrl: './users-table.component.html',
  styleUrl: './users-table.component.scss',
})
export class UsersTableComponent implements OnInit {
  isLoading = false;
  hidePagination = false;
  hideAllCard = false;
  lastNameSearchField: FormControl = new FormControl('');
  firstNameSearchField: FormControl = new FormControl('');
  emailSearchField: FormControl = new FormControl('');
  filterValuesUser = {
    firstName: '',
    lastName: '',
  };
  users: UserInfo[];
  dataSource: MatTableDataSource<UserInfo>;
  defaultColumns: string[] = ['firstName', 'lastName', 'officialemail'];
  displayedColumns: string[] = [...this.defaultColumns];
  hiddenColumns: string[] = [];
  expandedUser: UserInfo | null;
  isFilterActive = false;
  showFilters = true;

  contextStorage: {
    pageIndex: number;
    pageSize: number;
    length: number;
    nextPageLink: string;
    lastNameFilterValue: string;
    firstNameFilterValue: string;
    emailFilterValue: string;
  };

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  readonly usersType = input<'members' | 'owners'>(undefined);
  readonly title = input<string>(undefined);
  readonly entityId = input<string>(undefined);
  readonly paginator = viewChild(MatPaginator);

  constructor(
    private _graphService: GraphService,
    private _toolboxService: ToolBoxService
  ) {}

  ngOnInit() {
    this.setVisibleColumns();
    this.resetValues();
    this.getCollectionUsersPage(true);
  }

  createLicensesFilter(): (data: unknown, filter: string) => boolean {
    const filterFunction = (data, filter): boolean => {
      const searchTerms = JSON.parse(filter);
      return (
        data.skuPartNumber.toLowerCase().indexOf(searchTerms.displayName) !== -1
      );
    };
    return filterFunction;
  }

  async getCollectionUsersPage(isCallOnInit = false): Promise<void> {
    this.isLoading = true;
    await this._graphService
      .getCollectionUsers(
        this.entityId(),
        this.usersType(),
        this.contextStorage.nextPageLink
      )
      .subscribe({
        next: response => {
          this.hideAllCard = false;
          if (response.paginator.totalCount === 0) this.hideAllCard = true;
          this.setDataSources(response);
          if (isCallOnInit) {
            if (this.dataSource.filteredData.length === 0) {
              this.showFilters = false;
            } else {
              this.showFilters = true;
            }
          }
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  async getFilteredCollectionUsers(): Promise<void> {
    this.isLoading = true;
    await this._graphService
      .getFilteredCollectionUsers(
        this.entityId(),
        this.usersType(),
        this.firstNameFilterValue,
        this.lastNameFilterValue,
        this.emailFilterValue
      )
      .subscribe({
        next: response => {
          this.setDataSources(response);
        },
        error: () => {
          /**/
        },
      });
  }

  mapUsers(members: UserGraph[]): UserInfo[] {
    return members.map(userGraph => {
      const userInfo = new UserInfo();
      userInfo.officialemail = userGraph.userPrincipalName; // Mapper les propriétés correspondantes
      userInfo.firstName = userGraph.givenName;
      userInfo.lastName = userGraph.surname;
      return userInfo;
    });
  }

  setDataSources(response: CollectionUsersResponse) {
    if (response.paginator) {
      this.contextStorage.nextPageLink = response.paginator.nextPageLink;
      if (response.paginator.totalCount) {
        this.contextStorage.length = response.paginator.totalCount;
        this.hidePagination = false;
      } else if (response.paginator.totalCount === 0) {
        this.contextStorage.length = response.paginator.totalCount;
        this.hidePagination = true;
      }
    }

    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource<UserInfo>(
        this.mapUsers(response.users)
      );
    } else {
      this.dataSource.data = this.dataSource.data.concat(
        this.mapUsers(response.users)
      );
    }

    this.dataSource.paginator = this.paginator();
    setTimeout(() => {
      this.dataSource.paginator.length = this.contextStorage.length;
    });
    this.isLoading = false;
  }

  onPage(event: PageEvent): void {
    if (
      this.isFilterEmpty() &&
      event.pageIndex > this.contextStorage.pageIndex &&
      this.contextStorage.nextPageLink
    ) {
      this.contextStorage.pageIndex = event.pageIndex;
      this.getCollectionUsersPage();
    } else {
      this.contextStorage.pageIndex = event.pageIndex;
    }
  }

  setFilter(resetPageIndex = false) {
    this.contextStorage.firstNameFilterValue = this.firstNameFilterValue;
    this.contextStorage.lastNameFilterValue = this.lastNameFilterValue;
    this.contextStorage.emailFilterValue = this.emailFilterValue;
    this.dataSource = null;
    if (resetPageIndex) this.contextStorage.pageIndex = 0;
    this.isFilterActive = true;
  }

  async filterUsers(resetPageIndex = false) {
    this.setFilter(resetPageIndex);
    if (this.isFilterEmpty()) {
      this.reloadUsers(false);
      this.isFilterActive = false;
    } else {
      this.getFilteredCollectionUsers();
    }
  }

  reloadUsers(resetValues = true) {
    this.resetValues(resetValues);
    this.getCollectionUsersPage();
  }

  resetValues(resetFilters = true) {
    this.isLoading = false;
    this.dataSource = null;
    if (resetFilters) {
      this.firstNameSearchField = new FormControl('');
      this.lastNameSearchField = new FormControl('');
      this.emailSearchField = new FormControl('');
      this.isFilterActive = false;
    }
    this.resetContextStorage();
  }

  resetContextStorage() {
    this.contextStorage = {
      pageIndex: 0,
      pageSize: 5,
      length: null,
      nextPageLink: null,
      firstNameFilterValue: '',
      lastNameFilterValue: '',
      emailFilterValue: '',
    };
  }

  isFilterEmpty(): boolean {
    return (
      this.firstNameFilterValue === '' &&
      this.lastNameFilterValue === '' &&
      this.emailFilterValue === ''
    );
  }

  get firstNameFilterValue(): string {
    return this.firstNameSearchField.value;
  }

  set firstNameFilterValue(value: string) {
    this.firstNameSearchField.setValue(value);
  }

  get lastNameFilterValue(): string {
    return this.lastNameSearchField.value;
  }

  set lastNameFilterValue(value: string) {
    this.lastNameSearchField.setValue(value);
  }

  get emailFilterValue(): string {
    return this.emailSearchField.value;
  }

  set emailFilterValue(value: string) {
    this.emailSearchField.setValue(value);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setVisibleColumns();
  }

  setVisibleColumns() {
    this.displayedColumns = [...this.defaultColumns];
    this.hiddenColumns = [];
    this.expandedUser = null;

    if (window.innerWidth < 530) {
      this._toolboxService.hideTableColumn(
        'officialemail',
        this.displayedColumns,
        this.hiddenColumns
      );
    }
  }
}
